/**
 * InputBar — 채팅 입력 + 전송
 *
 * - Enter: 전송, Shift+Enter: 줄바꿈
 * - 2000자 카운터 (1500자 초과 시 노란색 경고, 초과 시 빨간색)
 * - disabled 상태 (스트리밍 중, rate limit 등)
 * - 첨부/도구 버튼 슬롯 (현재 stub)
 */

import { useRef, useState, useCallback } from "react";
import { Send, Square, Paperclip, Wrench } from "lucide-react";
import { cn } from "@/shared/lib/utils";

const MAX_LENGTH = 2000;
const WARN_LENGTH = 1500;

export interface InputBarProps {
  onSend: (text: string) => void;
  onStop?: () => void;
  isStreaming?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function InputBar({
  onSend,
  onStop,
  isStreaming = false,
  disabled = false,
  placeholder = "메시지를 입력하세요...",
  className,
}: InputBarProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const charCount = value.length;
  const isOverLimit = charCount > MAX_LENGTH;
  const isNearLimit = charCount >= WARN_LENGTH && !isOverLimit;

  // textarea 높이 자동 조절
  function autoResize() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (e.target.value.length <= MAX_LENGTH + 50) {
        setValue(e.target.value);
      }
      autoResize();
    },
    [],
  );

  const handleSend = useCallback(() => {
    const text = value.trim();
    if (!text || isOverLimit || disabled || isStreaming) return;
    onSend(text);
    setValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, isOverLimit, disabled, isStreaming, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const isInputDisabled = disabled && !isStreaming;
  const canSend = value.trim().length > 0 && !isOverLimit && !isInputDisabled;

  return (
    <div
      className={cn(
        "border-t border-stroke-subtle bg-surface-raised px-4 py-3",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-end gap-2 rounded-xl border bg-surface-base px-3 py-2.5 transition-colors",
          isInputDisabled
            ? "border-stroke-subtle opacity-60"
            : "border-stroke-subtle focus-within:border-accent/40 focus-within:ring-1 focus-within:ring-accent/20",
        )}
      >
        {/* 첨부 버튼 */}
        <button
          type="button"
          disabled={isInputDisabled}
          title="파일 첨부"
          className="flex size-7 shrink-0 items-center justify-center rounded-lg text-text-tertiary hover:bg-surface-secondary hover:text-text-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Paperclip className="size-4" />
        </button>

        {/* 도구 버튼 */}
        <button
          type="button"
          disabled={isInputDisabled}
          title="도구 선택"
          className="flex size-7 shrink-0 items-center justify-center rounded-lg text-text-tertiary hover:bg-surface-secondary hover:text-text-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Wrench className="size-4" />
        </button>

        {/* 텍스트 입력 */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isInputDisabled}
          placeholder={placeholder}
          rows={1}
          className={cn(
            "flex-1 resize-none bg-transparent text-[13px] leading-relaxed text-text-primary outline-none",
            "placeholder:text-text-tertiary",
            "disabled:cursor-not-allowed",
            "min-h-[28px] max-h-[160px]",
          )}
          style={{ overflowY: "auto" }}
        />

        {/* 글자 수 카운터 + 전송/중지 버튼 */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          {charCount > 0 && (
            <span
              className={cn(
                "text-[10px] font-medium leading-none",
                isOverLimit
                  ? "text-signal-danger"
                  : isNearLimit
                    ? "text-yellow-500"
                    : "text-text-tertiary",
              )}
            >
              {charCount}/{MAX_LENGTH}
            </span>
          )}

          {isStreaming ? (
            <button
              type="button"
              onClick={onStop}
              title="중지"
              className="flex size-7 items-center justify-center rounded-lg bg-signal-danger/10 text-signal-danger hover:bg-signal-danger/20 transition-colors"
            >
              <Square className="size-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              title="전송 (Enter)"
              className={cn(
                "flex size-7 items-center justify-center rounded-lg transition-colors",
                canSend
                  ? "bg-accent text-text-inverse hover:bg-accent/90"
                  : "bg-surface-secondary text-text-tertiary cursor-not-allowed",
              )}
            >
              <Send className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* 힌트 텍스트 */}
      <p className="mt-1.5 text-[10px] text-text-tertiary">
        Enter로 전송 · Shift+Enter로 줄바꿈
      </p>
    </div>
  );
}
